import { useQuery } from "@tanstack/react-query";
import { Button, Typography, Row, Col } from "antd";
import "./journal-section-six-info.scss";
import { journalsQueries } from "@/entities/journal";
import { useCoordinator } from "@/shared/lib/hooks";
import { fallbackString, formatDate } from "@shared/utils";

const { Title } = Typography;

interface IJournalSectionSixInfo {
  handleClose: () => void;
  itemData?: {
    key?: string;
    journalId?: string;
    sortedNumber?: string;
    confirmedNumber?: string;
    controlDate?: string;
    controlInfo?: string;
    expirationDate?: string;
    
    knmName?: string;
    knmNumber?: string;
    knmDate?: string;

    gsnRole?: string;
    gsnName?: string;
    gsnSurname?: string;
    gsnPatronymic?: string;

    expirationReason?: string;
    expirationDateFinish?: string;

    expirationName?: string;
    expirationSurname?: string;
    expirationPatronymic?: string;
    expirationRole?: string;
    expirationSignStatus?: string;
  };
}

export const JournalSectionSixInfo = ({
  handleClose,
  itemData,
}: IJournalSectionSixInfo) => {
  const { mainJournalId } = useCoordinator();

  const { data: mainJournalData } = useQuery(
    journalsQueries.getJournal({ mainJournalId }),
  );

  const { data: sectionSixData } = useQuery(
    journalsQueries.getSectionSixItem({ 
      sectionSixListId: mainJournalData?.mainJournal?.section6?.sectionListId || "",
      listItemId: itemData?.key || "", 
    }),
  );

  // Обрабатываем данные только после успешного выполнения useQuery
  if (!sectionSixData?.gsnInfo?.gsnInfo) {
    return (
      <div className="journal-object-wrapper">
        <div className="journal-object-container">
          <div className="journal-object-container-form">
            <div className="journal-section-six-info-header">
              <Title level={3} style={{ marginBottom: "4px", display: 'flex', flexDirection: "row", justifyContent: "space-between" }}>
                Просмотр записи
              </Title>
            </div>
            <div>Загрузка данных...</div>
            <Button type="primary" onClick={handleClose} style={{ marginTop: 32 }}>
              Закрыть
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Безопасное извлечение данных с проверками
  const { gsnInfo } = sectionSixData;
  const gsnInfoData = (gsnInfo as any)?.gsnInfo;
  const stateSupervisionList = gsnInfoData?.infoAboutStateSupervisionList?.[0];
  const stateSupervisionSignedData = stateSupervisionList?.stateSupervisionSignedData;
  const infoAboutStateSuperVisionSignedData = stateSupervisionSignedData?.infoAboutStateSupervisionSignedData;
  const stateSupervisionOfficialInfo = stateSupervisionSignedData?.stateSupervisionOfficialInfo;

  // Извлекаем данные с безопасными проверками
  const journalId = fallbackString(gsnInfoData?.journalId); 
  const sortedNumber = fallbackString(infoAboutStateSuperVisionSignedData?.sequenceNumber);
  const confirmedNumber = fallbackString(infoAboutStateSuperVisionSignedData?.controlEventRegistryNumber);
  const controlDate = fallbackString(infoAboutStateSuperVisionSignedData?.controlEventActInfo?.date); 
  const controlInfo = fallbackString(infoAboutStateSuperVisionSignedData?.controlEventResultsInfo);
  const expirationDate = fallbackString(infoAboutStateSuperVisionSignedData?.defectsFixingPlanDateInfo?.date);

  const knmName = fallbackString(infoAboutStateSuperVisionSignedData?.controlEventActInfo?.name); 
  const knmNumber = fallbackString(infoAboutStateSuperVisionSignedData?.controlEventActInfo?.number);
  const knmDate = fallbackString(infoAboutStateSuperVisionSignedData?.controlEventActInfo?.date);

  const gsnName = fallbackString(stateSupervisionOfficialInfo?.firstName); 
  const gsnSurname = fallbackString(stateSupervisionOfficialInfo?.lastName); 
  const gsnPatronymic = fallbackString(stateSupervisionOfficialInfo?.middleName);
  const gsnRole = fallbackString(stateSupervisionOfficialInfo?.position);

  const expirationReason = fallbackString(stateSupervisionSignedData?.defectControlDenialReason); 
  const expirationDateFinish = fallbackString(stateSupervisionSignedData?.defectsFixingFactDateInfo?.date); 

  const expirationName = fallbackString(infoAboutStateSuperVisionSignedData?.stateSupervisionOfficialInfo?.firstName);
  const expirationSurname = fallbackString(infoAboutStateSuperVisionSignedData?.stateSupervisionOfficialInfo?.lastName);
  const expirationPatronymic = fallbackString(infoAboutStateSuperVisionSignedData?.stateSupervisionOfficialInfo?.middleName);
  const expirationRole = fallbackString(infoAboutStateSuperVisionSignedData?.stateSupervisionOfficialInfo?.position);

  return (
    <div className="journal-object-wrapper">
      <div className="journal-object-container">
        <div className="journal-object-container-form">
            <div className="journal-section-six-info-header">
              <Title level={3} style={{ marginBottom: "4px", display: 'flex', flexDirection: "row", justifyContent: "space-between" }}>
                Просмотр записи
                <div className="title-sign-status">Подписано</div>
              </Title>
              <div className="journal-section-six-info-meta">
                Добавлено:
                <span className="journal-section-six-info-meta-value">02.01.2025</span>
              </div>
            </div>

            <div>
              <div className="journal-document-block-title">Сведения о контрольной процедуре</div>
              <Row gutter={[0, 16]} style={{ maxWidth: 680 }}>
                <Col span={12} style={{ color: "#888" }}>
                ID записи журнала
                </Col>
                <Col span={12}>{journalId}</Col>

                <Col span={12} style={{ color: "#888" }}>
                Порядковый номер записи
                </Col>
                <Col span={12}>
                  {sortedNumber}
                </Col>
                
                <Col span={12} style={{ color: "#888" }}>
                Учетный номер проверки
                </Col>
                <Col span={12}>
                  {confirmedNumber}
                </Col>
                                
                <Col span={12} style={{ color: "#888" }}>
                Дата контрольного (надзорного) мероприятия
                </Col>
                <Col span={12}>
                  {formatDate(controlDate as string)}
                </Col>
                <Col span={12} style={{ color: "#888" }}>
                Сведения о проведении строительного контроля
                </Col>
                <Col span={12}>
                  {controlInfo}
                </Col>
                <Col span={12} style={{ color: "#888" }}>
                Срок устранения недостатков
                </Col>
                <Col span={12}>
                  {formatDate(expirationDate as string)}
                </Col>
              </Row>

              <div className="journal-document-block-title">Реквизиты акта КНМ</div>
              <Row gutter={[0, 16]} style={{ maxWidth: 680 }}>
              <Col span={12} style={{ color: "#888" }}>Наименование документа</Col>
                <Col span={12}>{knmName}</Col>
                <Col span={12} style={{ color: "#888" }}>Номер/код/шифр документа</Col>
                <Col span={12}>{knmNumber}</Col>
                <Col span={12} style={{ color: "#888" }}> Дата документа</Col>
                <Col span={12}>{formatDate(knmDate as string)}</Col>
              </Row>

              <div className="journal-document-block-title">Должностное лицо государственного строительного надзора</div>
              <Row gutter={[0, 16]} style={{ maxWidth: 680, paddingBottom: "24px", borderBottom: '1px dashed #BCBFC0' }}>
              <Col span={12} style={{ color: "#888" }}>Фамилия</Col>
                <Col span={12}>{gsnSurname}</Col>
                <Col span={12} style={{ color: "#888" }}>Имя</Col>
                <Col span={12}>{gsnName}</Col>
                <Col span={12} style={{ color: "#888" }}>Отчество</Col>
                <Col span={12}>{gsnPatronymic}</Col>
                <Col span={12} style={{ color: "#888" }}>Должность</Col>
                <Col span={12}>{gsnRole}</Col>
              </Row>

              <div className="journal-document-block-title journal-document-block-title--large">Сведения об устранении выявленных нарушений</div>
              <Row gutter={[0, 16]} style={{ maxWidth: 680 }}>
              <Col span={12} style={{ color: "#888" }}>Причина снятия нарушения с контроля</Col>
                <Col span={12}>{expirationReason}</Col>
                <Col span={12} style={{ color: "#888" }}>Фактическая дата устранения</Col>
                <Col span={12}>{formatDate(expirationDateFinish as string)}</Col>
              </Row>

              <div className="journal-document-block-title">Должностное лицо государственного строительного надзора</div>
              <Row gutter={[0, 16]} style={{ maxWidth: 680, paddingBottom: "48px", borderBottom: '1px solid #BCBFC0' }}>
              <Col span={12} style={{ color: "#888" }}>Фамилия</Col>
                <Col span={12}>{expirationName}</Col>
                <Col span={12} style={{ color: "#888" }}>Имя</Col>
                <Col span={12}>{expirationSurname}</Col>
                <Col span={12} style={{ color: "#888" }}>Отчество</Col>
                <Col span={12}>{expirationPatronymic}</Col>
                <Col span={12} style={{ color: "#888" }}>Должность</Col>
                <Col span={12}>{expirationRole}</Col>
              </Row>
            </div>

            <Button
              type="primary"
              onClick={handleClose}
              style={{ marginTop: 32 }}
            >
              Закрыть
            </Button>
          </div>
        </div>
      </div>
  );
};
